import { Box, Button, CircularProgress, Snackbar } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Cancel, CheckCircle } from '@material-ui/icons';
import Alert from '@material-ui/lab/Alert';
import { API } from 'aws-amplify';
import { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import { useParams } from 'react-router-dom';
// Material

// Redux
import { properCase } from '../../../shared/helpers/string-helpers';
import {
  EnumAppDocFormat,
  EnumAppSignatureDisplayText,
  EnumAppSignatureFormat,
  EnumAppSignatureType,
  MApplication,
  MAppSignature
} from '../../redux/scholarship.models';

interface AppSummaryProps {
  scanningTranscript: boolean;
  scanningEssay: boolean;
  signatureOverrides?: MAppSignature[];
}

interface AppSummaryData {
  ttpf: MApplication['ttpf'];
  financial: MApplication['financial'];
  essay: MApplication['essay'];
  transcript: MApplication['transcript'];
  signatures: MApplication['signatures'];
}

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      fontWeight: 600
    },
    root: {
      width: '100%',
      marginBottom: '20px'
    }
  })
);

export const AppSummary = ({ scanningTranscript, scanningEssay, signatureOverrides }: AppSummaryProps): JSX.Element => {
  const classes = useStyles();
  const [appData, setAppData] = useState<AppSummaryData | undefined>(undefined);
  const [dataError, setDataError] = useState<string | undefined>(undefined);
  const [sendingEmail, setSendingEmail] = useState<boolean>(false);
  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const { applicationId } = useParams<{ applicationId: string }>();

  useEffect(() => {
    const fetchAppData = async () => {
      try {
        const { data } = (await API.get('service-mtpf', `/application/${applicationId}`, {})) as { data: AppSummaryData };
        setAppData(data);
      } catch (error) {
        console.error(error);
        setDataError(error instanceof Error ? error.message : String(error));
      }
    };
    void fetchAppData();
  }, [scanningTranscript, scanningEssay]);

  const handleResendEmail = async (val: MAppSignature): Promise<void> => {
    try {
      setSendingEmail(true);
      const body = {
        id: val.id,
        signatureType: val.signatureType,
        signatureId: val.signatureId
      };
      await API.post('service-mtpf', '/resend', { body: body });
      setSnackBarOpen(true);
      setSendingEmail(false);
    } catch (error) {
      console.error(error);
      setSendingEmail(false);
      setDataError(error instanceof Error ? error.message : String(error));
    }
  };

  const renderItemStatus = (val: boolean): JSX.Element => (
    <TableCell>{val ? <CheckCircle style={{ fill: 'green' }} /> : <Cancel style={{ fill: 'red' }} />}</TableCell>
  );

  const renderSignatureDisplayText = (signatureType: EnumAppSignatureType | null): string => {
    switch (signatureType) {
      case EnumAppSignatureType.AGREEMENT:
        return EnumAppSignatureDisplayText.AGREEMENT;
      case EnumAppSignatureType.PARENT:
        return EnumAppSignatureDisplayText.PARENT;
      case EnumAppSignatureType.PURCHASER:
        return EnumAppSignatureDisplayText.PURCHASER;
      case EnumAppSignatureType.CERTIFICATION:
        return EnumAppSignatureDisplayText.CERTIFICATION;
      default:
        return signatureType ? properCase(signatureType) : 'Unknown';
    }
  };

  const isSigned = (signedValue: unknown): boolean => {
    return signedValue === true || signedValue === 'true';
  };

  const renderDigitalSignature = (val: MAppSignature): JSX.Element => (
    <>
      {isSigned(val.signed) ? (
        <TableCell>{renderSignatureDisplayText(val.signatureType)}</TableCell>
      ) : (
        <TableCell>
          {(val.signatureType === EnumAppSignatureType.AGREEMENT || val.signatureType === EnumAppSignatureType.CERTIFICATION) &&
            renderSignatureDisplayText(val.signatureType)}
          {val.signatureType === EnumAppSignatureType.PARENT && 'Sign Below'}
          {val.signatureType === EnumAppSignatureType.PURCHASER && (
            <Button onClick={() => handleResendEmail(val)} variant="outlined" size="small" color="primary">
              Resend request
              {sendingEmail && (
                <span>
                  &emsp;
                  <CircularProgress size={12} color={'inherit'} />
                </span>
              )}
            </Button>
          )}
        </TableCell>
      )}
    </>
  );

  const renderSignature = (val: MAppSignature, index: number): JSX.Element => (
    <TableRow key={index}>
      <TableCell>&emsp;{`${val.firstName} ${val.lastName}`}</TableCell>
      {val.signatureFormat === EnumAppSignatureFormat.DIGITAL ? (
        renderDigitalSignature(val)
      ) : (
        <TableCell>{renderSignatureDisplayText(val.signatureType)}</TableCell>
      )}
      {renderItemStatus(isSigned(val.signed))}
    </TableRow>
  );

  const renderAppSummary = (summaryData: AppSummaryData): JSX.Element => (
    <Table size="small">
      <TableBody>
        <TableRow>
          <TableCell className={classes.title} colSpan={12}>
            TTPF Account
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>&emsp;Account ID</TableCell>
          <TableCell>{summaryData.ttpf?.ttpfId || '—'}</TableCell>
          {renderItemStatus(!!summaryData.ttpf?.ttpfId)}
        </TableRow>
        <TableRow>
          <TableCell>&emsp;Account Owner</TableCell>
          <TableCell>
            {summaryData.ttpf?.ttpfOwnerFirstName ? `${summaryData.ttpf?.ttpfOwnerFirstName} ${summaryData.ttpf?.ttpfOwnerLastName}` : '—'}
          </TableCell>
          {renderItemStatus(!!summaryData.ttpf?.ttpfOwnerFirstName)}
        </TableRow>
        <TableRow>
          <TableCell className={classes.title} colSpan={12}>
            Financial
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>&emsp;Dependents</TableCell>
          <TableCell>{summaryData.financial?.dependents || '—'}</TableCell>
          {renderItemStatus(!!summaryData.financial?.dependents)}
        </TableRow>
        <TableRow>
          <TableCell>&emsp;Income</TableCell>
          <TableCell>
            {summaryData.financial?.income ? (
              <NumberFormat value={summaryData.financial?.income} thousandSeparator={true} prefix={'$'} displayType={'text'} />
            ) : (
              '—'
            )}
          </TableCell>
          {renderItemStatus(!!summaryData.financial?.income)}
        </TableRow>
        <TableRow>
          <TableCell className={classes.title}>Essay</TableCell>
          <TableCell></TableCell>
          {summaryData.essay
            ? summaryData.essay.docFormat === EnumAppDocFormat.TEXT
              ? renderItemStatus(!!summaryData.essay?.docContent)
              : renderItemStatus(!!summaryData.essay?.filePath)
            : renderItemStatus(false)}
        </TableRow>
        <TableRow>
          <TableCell className={classes.title}>Transcript</TableCell>
          <TableCell></TableCell>
          {scanningTranscript ? (
            <TableCell>
              <CircularProgress size={20} />{' '}
            </TableCell>
          ) : summaryData.transcript ? (
            renderItemStatus(!!summaryData.transcript?.filePath)
          ) : (
            renderItemStatus(false)
          )}
        </TableRow>
        <TableRow>
          <TableCell className={classes.title} colSpan={12}>
            Signatures
          </TableCell>
        </TableRow>
        {(signatureOverrides || summaryData.signatures || []).map(renderSignature)}
      </TableBody>
    </Table>
  );

  return (
    <div className={classes.root}>
      {dataError && (
        <Alert severity="error" onClose={() => setDataError(undefined)}>
          {dataError}
        </Alert>
      )}
      {!appData && (
        <Box m="auto">
          <CircularProgress />
        </Box>
      )}
      <TableContainer component={Paper}>{appData && renderAppSummary(appData)}</TableContainer>
      <Snackbar
        open={snackBarOpen}
        onClose={() => setSnackBarOpen(false)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackBarOpen(false)} severity="success">
          Email re-sent successfully!
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AppSummary;
